import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useSettings } from '../hooks/useSettings';
import { SettingsHeader } from './settings/SettingsHeader';
import { SettingsProfile } from './settings/SettingsProfile';
import { SettingsDefaults } from './settings/SettingsDefaults';
import { SettingsSecurity } from './settings/SettingsSecurity';
import { SettingsPreferences } from './settings/SettingsPreferences';
import { SettingsAppInfo } from './settings/SettingsAppInfo';
import { SettingsDangerZone } from './settings/SettingsDangerZone';
import { SecureAccountModal } from './settings/modals/SecureAccountModal';
import { LoginModal } from './settings/modals/LoginModal';

export const SettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    user,
    theme, toggleTheme,
    isLoadingProfile,

    // Profile
    displayName, setDisplayName,
    isEditingName, setIsEditingName,
    handleUpdateName, handleCancelEditName,

    // Payment
    paymentAppId, setPaymentAppId,
    paymentUsername, setPaymentUsername,
    isEditingPayment, setIsEditingPayment,
    handleUpdatePayment, handleCancelEditPayment,

    // Preferences
    preferredCurrency,
    handleCurrencyChange,

    // Auth Actions
    handleLogout,
    handleLinkEmail,
    handleLogin,
    handleSignUp,
    resetForm,

    // Modal State
    isSecureModalOpen, setIsSecureModalOpen,
    isLoginModalOpen, setIsLoginModalOpen,
    loginModalMode, setLoginModalMode,

    // Form State
    email, setEmail,
    password, setPassword,
    isSubmitting
  } = useSettings();

  // Check for auto-login param
  useEffect(() => {
    if (searchParams.get('action') === 'login') {
      // Clear param and open modal
      setSearchParams(params => {
        params.delete('action');
        return params;
      });
      setIsLoginModalOpen(true);
    }
  }, [searchParams, setSearchParams, setIsLoginModalOpen]);

  return (
    <div className="h-full flex flex-col">
      <SettingsHeader />

      {isLoadingProfile ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          <div className="rounded-2xl border border-border-default bg-bg-card overflow-hidden shadow-sm">
            <SettingsProfile
              user={user}
              displayName={displayName}
              setDisplayName={setDisplayName}
              isEditingName={isEditingName}
              setIsEditingName={setIsEditingName}
              onUpdateName={handleUpdateName}
              onCancelEditName={handleCancelEditName}
              onLogout={handleLogout}
            />

            <div className="p-4 border-b border-border-default">
              <SettingsSecurity
                user={user}
                onSecure={() => { setIsSecureModalOpen(true); }}
                onLogin={() => {
                  resetForm();
                  setLoginModalMode('login');
                  setIsLoginModalOpen(true);
                }}
                onSignUp={() => {
                  resetForm();
                  setLoginModalMode('signup');
                  setIsLoginModalOpen(true);
                }}
              />
            </div>

            <SettingsDefaults
              paymentAppId={paymentAppId}
              setPaymentAppId={setPaymentAppId}
              paymentUsername={paymentUsername}
              setPaymentUsername={setPaymentUsername}
              isEditingPayment={isEditingPayment}
              setIsEditingPayment={setIsEditingPayment}
              onUpdatePayment={handleUpdatePayment}
              onCancelEditPayment={handleCancelEditPayment}
              preferredCurrency={preferredCurrency}
              onCurrencyChange={handleCurrencyChange}
            />

            <div className="p-4 space-y-4 border-b border-border-default">
              <SettingsPreferences
                theme={theme}
                toggleTheme={toggleTheme}
              />
            </div>
            
            <SettingsDangerZone user={user} />
          </div>

          <SettingsAppInfo theme={theme} />
        </div>
      )}

      <SecureAccountModal
        isOpen={isSecureModalOpen}
        onClose={() => { setIsSecureModalOpen(false); }}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        isSubmitting={isSubmitting}
        onSubmit={handleLinkEmail}
      />

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => { setIsLoginModalOpen(false); }}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        isSubmitting={isSubmitting}
        onSubmit={handleLogin}
        onSignUp={handleSignUp}
        initialMode={loginModalMode}
      />
    </div>
  );
};
